/**
 * Beam presence follows the visitor across pages (PRESENCE-WINS law).
 *
 * Deployment rules are per path: the home page may be the Beam deploy
 * while every other page resolves to a chat button or nothing at all.
 * Once the standing agent has been on stage in this session, yanking
 * her off on the next click reads as a broken product (David, Lumina
 * 2026-09-04: "when it navigates page, beam does not show"). So the
 * stage stamps a session marker on mount, and discover + config honour
 * it on every later page of the same session for the same agent —
 * provided the server still says the agent can serve Beam
 * (`voice.presentation === 'boundless'`, the eligibility gate).
 *
 * sessionStorage: per tab, survives same-site navigation, dies with the
 * tab — exactly the visit. Pure over an injected storage for tests.
 */

export const BEAM_PRESENCE_KEY = 'sp_beam_presence';

export interface BeamPresence {
  agentPublicId: string;
  at: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function markBeamPresence(storage: StorageLike | null | undefined, agentPublicId: string, now = Date.now()): void {
  if (!storage || !agentPublicId) return;
  try {
    storage.setItem(BEAM_PRESENCE_KEY, JSON.stringify({ agentPublicId, at: now } satisfies BeamPresence));
  } catch {
    /* storage unavailable (private mode, quota) — presence just won't carry */
  }
}

export function readBeamPresence(storage: StorageLike | null | undefined): BeamPresence | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BEAM_PRESENCE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<BeamPresence>;
    if (typeof v.agentPublicId !== 'string' || !v.agentPublicId) return null;
    return { agentPublicId: v.agentPublicId, at: typeof v.at === 'number' ? v.at : 0 };
  } catch {
    return null;
  }
}

export function clearBeamPresence(storage: StorageLike | null | undefined): void {
  try {
    storage?.removeItem(BEAM_PRESENCE_KEY);
  } catch {
    /* ignore */
  }
}

/** Presence carries for THIS agent only — a different agent on the page is not her. */
export function beamPresenceFor(storage: StorageLike | null | undefined, agentPublicId: string): boolean {
  const p = readBeamPresence(storage);
  return !!p && p.agentPublicId === agentPublicId;
}

export function sessionStorageOrNull(): StorageLike | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}
